import React, { useEffect, useRef, useState } from "react";
import Layout from "../../components/layouts/Layout";
import BreadcrumbBlock from "../../components/BreadcrumbBlock/BreadcrumbBlock";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  DatePicker,
  Flex,
  Table,
  TableColumnsType,
  notification,
} from "antd";
import { Calendar2, DocumentDownload, Home2 } from "iconsax-react";
import { EyeOutlined, FilterOutlined } from "@ant-design/icons";
import { mixPostV1 } from "../../services/mixin";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { RangePickerProps } from "antd/es/date-picker";
import "./WeeklyReport.scss";
import * as XLSX from "xlsx";
import ShiftFilterModal, { ShiftDataType } from "./Components/ShiftFilterModal";

const { RangePicker } = DatePicker;

const disabledDate: RangePickerProps["disabledDate"] = (current) =>
  current && current > dayjs().endOf("day");

const numberFormat = (number: any) => {
  if (typeof number === "number") {
    return new Intl.NumberFormat().format(number);
  }
  return number;
};

interface DayRow {
  date: string;
  date_label: string;
  final: number;
  loss: number;
  bonus: number;
  labor: number;
  match: number;
  extra: number;
  zelle: number;
  baccarat: number;
}

interface WeeklySummary {
  bank_total: number;
  bonus_total: number;
  cash_on_hand_total: number;
  extra_total: number;
  labor_total: number;
  match_total: number;
  money_of_system_total: number;
  payout_total: number;
  profit_machine_total: number;
  total_final: number;
  total_loss: number;
  total_profit: number;
  zelle_total: number;
  baccarat_total: number;
}

const defaultSummary: WeeklySummary = {
  total_final: 0,
  total_loss: 0,
  total_profit: 0,
  bonus_total: 0,
  labor_total: 0,
  match_total: 0,
  extra_total: 0,
  payout_total: 0,
  bank_total: 0,
  profit_machine_total: 0,
  money_of_system_total: 0,
  cash_on_hand_total: 0,
  zelle_total: 0,
  baccarat_total: 0,
};

const WeeklyReport: React.FC = () => {
  const navigate = useNavigate();
  const can_view_weekly_report =
    localStorage.getItem("can_view_weekly_report") ?? "0";
  const [tableLoading, setTableLoading] = useState(false);
  const [items, setItems] = useState<DayRow[]>([]);
  const [summary, setSummary] = useState<WeeklySummary>(defaultSummary);
  const [hiddenBonus, setHiddenBonus] = useState(false);
  const [hiddenMatch, setHiddenMatch] = useState(false);
  const [hiddenExtra, setHiddenExtra] = useState(false);
  const [hiddenBank, setHiddenBank] = useState(false);
  const [hiddenMoney, setHiddenMoney] = useState(false);
  const [hiddenCash, setHiddenCash] = useState(false);

  // Original unfiltered data
  const [originalItems, setOriginalItems] = useState<DayRow[]>([]);
  const [originalSummary, setOriginalSummary] =
    useState<WeeklySummary>(defaultSummary);

  // Filter states
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);
  const [selectedShiftKeys, setSelectedShiftKeys] = useState<React.Key[]>([]);
  const [selectedShifts, setSelectedShifts] = useState<ShiftDataType[]>([]);

  const defaultStart = dayjs().startOf("week").add(1, "day");
  const defaultEnd = dayjs().endOf("week").add(1, "day");
  const [rangeValue, setRangeValue] = useState<[Dayjs, Dayjs]>([
    defaultStart,
    defaultEnd,
  ]);
  const isFetched = useRef(false);

  const [api, contextHolder] = notification.useNotification();
  const openNotification = (type: "success" | "error", message: string) => {
    api[type]({
      message,
      placement: "bottomRight",
      className: "single-message",
    });
  };

  const getItems = async (
    start: Dayjs,
    end: Dayjs,
    reportIds?: React.Key[],
  ) => {
    setTableLoading(true);
    const params: any = {
      start_date: start.format("YYYY-MM-DD"),
      end_date: end.format("YYYY-MM-DD"),
    };
    if (reportIds && reportIds.length > 0) {
      params.report_ids = reportIds.join(",");
    }
    const res = await mixPostV1(
      "/api/webAdmin/weeklyReportSumaryByDate.php",
      params,
    );
    setTableLoading(false);
    if (res.status === 1) {
      const reportDetail = res.data?.report_detail ?? [];
      const summaryAll = {
        ...defaultSummary,
        ...(res.data?.summary_all_period ?? {}),
      };
      setItems(reportDetail);
      setSummary(summaryAll);

      if (reportIds && reportIds.length > 0) {
        setIsFiltered(true);
      } else {
        setOriginalItems(reportDetail);
        setOriginalSummary(summaryAll);
        // Reset filtering state on date range change
        setIsFiltered(false);
        setSelectedShiftKeys([]);
        setSelectedShifts([]);
      }
    } else {
      openNotification("error", res.message ?? "Failed to load data");
    }
  };

  useEffect(() => {
    if (Number(can_view_weekly_report) === 0) {
      navigate("/");
    }
    if (!isFetched.current) {
      isFetched.current = true;
      getItems(rangeValue[0], rangeValue[1]);
    }
  }, []);

  const handleRangeChange = (dates: any) => {
    if (dates && dates[0] && dates[1]) {
      const range: [Dayjs, Dayjs] = [dates[0], dates[1]];
      setRangeValue(range);
      getItems(range[0], range[1]);
    }
  };

  const handleFilterSubmit = (keys: React.Key[], rows: ShiftDataType[]) => {
    setIsFilterModalOpen(false);
    setSelectedShiftKeys(keys);
    setSelectedShifts(rows);

    if (keys.length === 0) {
      setItems(originalItems);
      setSummary(originalSummary);
      setIsFiltered(false);
      return;
    }

    getItems(rangeValue[0], rangeValue[1], keys);
  };

  const handleClearFilter = () => {
    setItems(originalItems);
    setSummary(originalSummary);
    setIsFiltered(false);
    setSelectedShiftKeys([]);
    setSelectedShifts([]);
  };

  const handleExportExcel = () => {
    if (items.length === 0) {
      openNotification("error", "There is no data to export!");
      return;
    }

    try {
      // 1. Format details rows
      const formattedData = items.map((item) => ({
        Date: item.date_label,
        Final: item.final,
        "Short / Loss":
          item.loss + Number(item.zelle) - (Number(item.baccarat) || 0),
        Bonus: item.bonus,
        Labor: item.labor,
        Match: item.match,
        Extra: item.extra,
        Zelle: item.zelle,
        Baccarat: item.baccarat,
        "Net Profit":
          item.final +
          item.loss -
          (item.bonus + item.labor + item.match + item.extra),
      }));

      // 2. Append the TOTAL row
      formattedData.push({
        Date: "TOTAL",
        Final: summary.total_final,
        "Short / Loss": summary.total_loss,
        Bonus: summary.bonus_total,
        Labor: summary.labor_total,
        Match: summary.match_total,
        Extra: summary.extra_total,
        Zelle: summary.zelle_total,
        Baccarat: summary.baccarat_total,
        "Net Profit":
          summary.total_final +
          summary.total_loss -
          (summary.bonus_total +
            summary.labor_total +
            summary.match_total +
            summary.extra_total +
            summary.zelle_total +
            summary.baccarat_total),
      });

      const worksheet = XLSX.utils.json_to_sheet(formattedData);

      // 3. Add a section for row 2 metrics (System Summary)
      const systemSummaryAOA = [
        [],
        ["SYSTEM SUMMARY"],
        ["Total Payout of System", summary.payout_total],
        ["Total Bank", summary.bank_total],
        ["Total Profit Machine", summary.profit_machine_total],
        ["Total Money of System", summary.money_of_system_total],
        ["Total Cash on Hand", summary.cash_on_hand_total],
      ];

      XLSX.utils.sheet_add_aoa(worksheet, systemSummaryAOA, { origin: -1 });

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Weekly Report");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "buffer",
      });

      const saveAsExcel = (buffer: ArrayBuffer, fileName: string): void => {
        const data = new Blob([buffer], { type: "application/octet-stream" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(data);
        link.download = fileName;
        link.click();
      };

      const dateStr = `${rangeValue[0].format("YYYYMMDD")}_${rangeValue[1].format("YYYYMMDD")}`;
      saveAsExcel(excelBuffer, `Weekly_Report_${dateStr}.xlsx`);
      openNotification("success", "Exported successfully!");
    } catch (error) {
      console.error(error);
      openNotification("error", "Failed to export Excel!");
    }
  };

  const columns: TableColumnsType<DayRow> = [
    {
      title: "Date",
      dataIndex: "date_label",
      key: "date_label",
      fixed: "left",
      width: 110,
      render: (text, record) => (
        <Flex align="center" gap={6}>
          <span>{text}</span>
        </Flex>
      ),
    },
    {
      title: "Final Machine",
      dataIndex: "final",
      key: "final",
      align: "right",
      width: 100,
      render: (v) => (
        <span className={v >= 0 ? "text-green" : "text-red"}>
          {numberFormat(v)}
        </span>
      ),
    },
    {
      title: "Zelle",
      dataIndex: "zelle",
      key: "zelle",
      align: "right",
      width: 90,
      render: (v) => (
        <span className={v >= 0 ? "text-green" : "text-red"}>
          {numberFormat(v)}
        </span>
      ),
    },
    {
      title: "Baccarat",
      dataIndex: "baccarat",
      key: "baccarat",
      align: "right",
      width: 90,
      render: (v) => (
        <span className={v >= 0 ? "text-green" : "text-red"}>
          {numberFormat(v)}
        </span>
      ),
    },
    {
      title: "Short / Loss",
      dataIndex: "loss",
      key: "loss",
      align: "right",
      width: 110,
      render: (v, record) => (
        <span
          className={
            Number(v) +
              Number(record?.zelle) -
              (Number(record?.baccarat) || 0) >=
            0
              ? "text-green"
              : "text-red"
          }
        >
          {numberFormat(
            Number(v) + Number(record?.zelle) - (Number(record?.baccarat) || 0),
          )}
        </span>
      ),
    },
    {
      title: (
        <Flex align="center" gap={4} justify="end">
          <span>Bonus</span>
          <span className="wr-eye" onClick={() => setHiddenBonus(!hiddenBonus)}>
            <EyeOutlined />
          </span>
        </Flex>
      ),
      dataIndex: "bonus",
      key: "bonus",
      align: "right",
      width: 90,
      render: (v) =>
        hiddenBonus ? (
          <span className="text-idle">***</span>
        ) : (
          <span>{numberFormat(v)}</span>
        ),
    },
    {
      title: "Labor",
      dataIndex: "labor",
      key: "labor",
      align: "right",
      width: 90,
      render: (v) => <span>{numberFormat(v)}</span>,
    },
    {
      title: (
        <Flex align="center" gap={4} justify="end">
          <span>Match</span>
          <span className="wr-eye" onClick={() => setHiddenMatch(!hiddenMatch)}>
            <EyeOutlined />
          </span>
        </Flex>
      ),
      dataIndex: "match",
      key: "match",
      align: "right",
      width: 90,
      render: (v) =>
        hiddenMatch ? (
          <span className="text-idle">***</span>
        ) : (
          <span>{numberFormat(v)}</span>
        ),
    },
    {
      title: (
        <Flex align="center" gap={4} justify="end">
          <span>Extra</span>
          <span className="wr-eye" onClick={() => setHiddenExtra(!hiddenExtra)}>
            <EyeOutlined />
          </span>
        </Flex>
      ),
      dataIndex: "extra",
      key: "extra",
      align: "right",
      width: 80,
      render: (v) =>
        hiddenExtra ? (
          <span className="text-idle">***</span>
        ) : (
          <span>{numberFormat(v)}</span>
        ),
    },
    {
      title: "Net Profit",
      dataIndex: "net_profit",
      key: "net_profit",
      align: "right",
      width: 100,
      render: (v, record: any) => {
        const val = record.final + record.baccarat;
        return (
          <span className={val >= 0 ? "text-green" : "text-red"}>
            {numberFormat(val)}
          </span>
        );
      },
    },
  ];

  const totalNetProfit =
    Number(summary.total_final) + Number(summary.baccarat_total);

  const metricCards = [
    {
      label: "Total Final",
      value: summary.total_final,
      className: summary.total_final >= 0 ? "text-green" : "text-red",
    },
    {
      label: "Total Short / Loss",
      value:
        Number(summary.total_loss) +
        Number(summary?.zelle_total) -
        Number(summary?.baccarat_total || 0),
      className: summary.total_loss >= 0 ? "text-green" : "text-red",
    },
    {
      label: "Net Profit",
      value: totalNetProfit,
      className: totalNetProfit >= 0 ? "text-green" : "text-red",
    },
    {
      label: "Total Bonus",
      value: summary.bonus_total,
      hidden: hiddenBonus,
      eye: true,
      onEye: () => setHiddenBonus(!hiddenBonus),
    },
    { label: "Total Labor", value: summary.labor_total },
    {
      label: "Total Match",
      value: summary.match_total,
      hidden: hiddenMatch,
      eye: true,
      onEye: () => setHiddenMatch(!hiddenMatch),
    },
    {
      label: "Total Extra",
      value: summary.extra_total,
      hidden: hiddenExtra,
      eye: true,
      onEye: () => setHiddenExtra(!hiddenExtra),
    },
  ];

  const metricCards2 = [
    { label: "Total Zelle", value: summary.zelle_total },
    { label: "Total Baccarat", value: summary.baccarat_total },
    { label: "Total Payout of System", value: summary.payout_total },

    {
      label: "Total Bank",
      value: summary.bank_total,
      hidden: hiddenBank,
      eye: true,
      onEye: () => setHiddenBank(!hiddenBank),
    },
    { label: "Total Profit Machine", value: summary.profit_machine_total },
    {
      label: "Total Money of System",
      value: summary.money_of_system_total,
      hidden: hiddenMoney,
      eye: true,
      onEye: () => setHiddenMoney(!hiddenMoney),
    },
    {
      label: "Total Cash on Hand",
      value: summary.cash_on_hand_total,
      hidden: hiddenCash,
      eye: true,
      onEye: () => setHiddenCash(!hiddenCash),
    },
  ];

  return (
    <Layout contentClassName="report-weekly-content">
      {contextHolder}

      <BreadcrumbBlock
        items={[
          {
            title: (
              <Flex align="center" gap={16}>
                <Home2 variant="Bulk" />
                <Link to="/">CMD Tech</Link>
              </Flex>
            ),
          },
          { title: "Report" },
          { title: "Weekly Report" },
        ]}
        title="Weekly Report"
        description=""
      />

      <Flex
        align="flex-start"
        justify="space-between"
        className="wr-header"
        wrap="wrap"
        gap={12}
      >
        <div>
          <p className="wr-subtitle"></p>
        </div>
        <Flex align="center" gap={12}>
          <RangePicker
            className="wr-range-picker"
            value={rangeValue}
            onChange={handleRangeChange}
            disabledDate={disabledDate}
            format="MM/DD/YYYY"
            suffixIcon={<Calendar2 size={16} />}
          />
          <Button
            className={`rs-button wr-filter-btn ${isFiltered ? "active" : ""}`}
            icon={<FilterOutlined />}
            onClick={() => setIsFilterModalOpen(true)}
            style={{
              height: 40,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {isFiltered
              ? `Selected (${selectedShiftKeys.length})`
              : "Filter by shifts"}
          </Button>
          {isFiltered && (
            <Button
              className="rs-button wr-clear-btn"
              onClick={handleClearFilter}
              style={{ height: 40 }}
            >
              Clear Filter
            </Button>
          )}
          <Button
            className="rs-button wr-export-btn"
            icon={<DocumentDownload size={18} />}
            onClick={handleExportExcel}
          >
            Export
          </Button>
        </Flex>
      </Flex>

      {/* Row 1 metrics */}
      <div className="wr-metrics-row1">
        {metricCards.map((card, i) => (
          <div key={i} className="wr-metric-card">
            <Flex align="center" gap={4} className="wr-metric-label">
              <span>{card.label}</span>
              {card.eye && (
                <span className="wr-eye" onClick={card.onEye}>
                  <EyeOutlined />
                </span>
              )}
            </Flex>
            <div className={`wr-metric-value ${card.className ?? ""}`}>
              {card.hidden ? "***" : numberFormat(card.value)}
            </div>
          </div>
        ))}
      </div>

      {/* Row 2 metrics */}
      <div className="wr-metrics-row2">
        {metricCards2.map((card, i) => (
          <div key={i} className="wr-metric-card">
            <Flex align="center" gap={4} className="wr-metric-label">
              <span>{card.label}</span>
              {card.eye && (
                <span className="wr-eye" onClick={card.onEye}>
                  <EyeOutlined />
                </span>
              )}
            </Flex>
            <div className="wr-metric-value">
              {card.hidden ? "***" : numberFormat(card.value)}
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="t-table-block wr-table-block">
        <Table
          columns={columns}
          dataSource={items}
          loading={tableLoading}
          pagination={false}
          scroll={{ x: true }}
          bordered={false}
        />
      </div>

      <ShiftFilterModal
        isShow={isFilterModalOpen}
        fromDate={rangeValue[0]}
        toDate={rangeValue[1]}
        initialSelectedKeys={selectedShiftKeys}
        onClose={() => setIsFilterModalOpen(false)}
        onSubmit={handleFilterSubmit}
      />
    </Layout>
  );
};

export default WeeklyReport;

